import {
  ArrowLeft,
  ChevronRight,
  BookOpenCheck,
  FileText,
  FolderOpen,
  Home,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Route,
  Settings2,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useCompactStudyLayout } from './studyMobile';
import { useNavigationVisibility } from '@/components/navigation/useNavigationVisibility';
import '@/components/navigation/navigationStrip.css';
import { Link, useLocation } from 'react-router-dom';
import { STUDY_JOURNEY_PATH, STUDY_SETTINGS_PATH } from './studyYearRoutes';

/**
 * "My year" is retired (study-my-year-prd.md §3.1): Home carries the Term, deadlines and wrap-up.
 * The strip keeps the pages used every day. Pages opened rarely (Review, My journey, Study
 * settings) sit under More (owner decision 2026-10-09: "put the less important ones in More").
 */
const links = [
  { to: '/learn/study', label: 'Today', icon: Home, end: true },
  // Keep the reference library visible on narrow screens before the strip overflows.
  { to: '/learn/study/cheatsheets', label: 'Cheatsheets', icon: FileText },
  { to: '/learn/study/materials', label: 'Materials', icon: FolderOpen },
  { to: '/learn/study/subjects', label: 'Subjects', icon: BookOpenCheck },
];

const STUDY_MORE_LINKS = [
  { to: '/learn/study/working', label: 'My working', icon: Pencil },
  { to: '/learn/study/review', label: 'Review', icon: RotateCcw },
  { to: STUDY_JOURNEY_PATH, label: 'My journey', icon: Route },
  { to: STUDY_SETTINGS_PATH, label: 'Study settings', icon: Settings2 },
];

const isAt = (pathname: string, to: string) => pathname === to || pathname.startsWith(`${to}/`);

/**
 * More: a menu of the rarely used Study pages. The strip scrolls sideways (it clips its
 * children), so the menu is placed with fixed coordinates under the button.
 */
function StudyMoreMenu({ pathname }: { pathname: string }) {
  const [open, setOpen] = useState(false);
  const compact = useCompactStudyLayout();
  const titleId = useId();
  const sheet = useRef<HTMLDialogElement>(null);
  const [place, setPlace] = useState({ top: 0, right: 0 });
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const current = STUDY_MORE_LINKS.some(({ to }) => isAt(pathname, to));

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const dialog = sheet.current;
    if (!compact || !open) {
      dialog?.close();
      return;
    }
    dialog?.showModal();
    return () => dialog?.close();
  }, [compact, open]);
  useEffect(() => {
    if (!open || compact) return undefined;
    const close = (event: Event) => {
      const target = event.target as Node | null;
      if (target && (menu.current?.contains(target) || button.current?.contains(target))) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
    };
  }, [open, compact]);

  const toggle = () => {
    const box = button.current?.getBoundingClientRect();
    if (box) setPlace({ top: box.bottom + 8, right: Math.max(8, window.innerWidth - box.right) });
    setOpen((value) => !value);
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        onClick={toggle}
        aria-haspopup={compact ? 'dialog' : 'menu'}
        aria-expanded={open}
        aria-current={current ? 'page' : undefined}
        className={`flex min-h-11 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full px-2 py-2 text-sm font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink ${
          current || open ? 'bg-wash-sky text-ink' : 'text-slate2 hover:bg-surface hover:text-ink'
        }`}
      >
        <MoreHorizontal className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>More</span>
      </button>
      {compact && (
        <dialog
          ref={sheet}
          className="study-more-sheet"
          aria-labelledby={titleId}
          onCancel={() => setOpen(false)}
          onClose={() => {
            setOpen(false);
            button.current?.focus();
          }}
        >
          <header className="flex items-center gap-3 border-b border-hairline p-3">
            <button
              type="button"
              aria-label="Close Study menu"
              onClick={() => setOpen(false)}
              className="grid h-11 w-11 place-items-center rounded-full bg-surface text-ink"
            >
              <ArrowLeft size={20} aria-hidden="true" />
            </button>
            <h2 id={titleId} className="text-lg font-extrabold text-ink">
              More in Study
            </h2>
          </header>
          <nav aria-label="More Study pages" className="p-4">
            {STUDY_MORE_LINKS.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                aria-current={isAt(pathname, to) ? 'page' : undefined}
                className="study-more-row flex min-h-16 items-center gap-3 border-b border-hairline px-3 py-4 text-base font-bold text-ink"
              >
                <span className="grid h-10 w-10 place-items-center rounded-2xl bg-wash-sky">
                  <Icon size={20} aria-hidden="true" />
                </span>
                <span className="flex-1">{label}</span>
                <ChevronRight size={18} className="text-slate2" aria-hidden="true" />
              </Link>
            ))}
          </nav>
        </dialog>
      )}
      {open && !compact && (
        <div
          ref={menu}
          role="menu"
          aria-label="More in Study"
          style={{ top: place.top, right: place.right }}
          className="fixed z-[70] w-56 rounded-2xl border border-hairline bg-canvas-pure p-1 shadow-floating-chrome"
        >
          {STUDY_MORE_LINKS.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              role="menuitem"
              aria-current={isAt(pathname, to) ? 'page' : undefined}
              onClick={() => setOpen(false)}
              className={`flex min-h-11 items-center gap-3 rounded-xl px-3 py-2 text-sm font-bold ${
                isAt(pathname, to) ? 'bg-wash-sky text-ink' : 'text-ink hover:bg-surface'
              }`}
            >
              <Icon className="h-5 w-5 shrink-0 text-slate2" aria-hidden="true" />
              {label}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

export function StudyNavigation({ mobile = false }: { mobile?: boolean }) {
  const { pathname } = useLocation();
  const { navigation, revealFocusedLink } = useNavigationVisibility(pathname);

  return (
    <nav
      ref={navigation}
      aria-label={mobile ? 'Study mobile navigation' : 'Study navigation'}
      data-study-tour="navigation"
      onFocusCapture={revealFocusedLink}
      className={`navigation-strip min-w-0 items-center gap-1 overflow-x-auto overscroll-x-contain p-1 ${
        mobile
          ? 'study-mobile-navigation flex rounded-full border border-hairline bg-surface'
          : 'study-desktop-navigation flex-1'
      }`}
    >
      {links.map(({ to, label, icon: Icon, end }) => {
        const active =
          pathname === to ||
          (!end && pathname.startsWith(`${to}/`)) ||
          (to === '/learn/study/materials' && pathname.startsWith('/learn/study/notes'));
        return (
          <Link
            key={to}
            to={to}
            aria-current={active ? 'page' : undefined}
            aria-label={label}
            className={`flex min-h-11 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full px-2 py-2 text-sm font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink ${
              active ? 'bg-wash-sky text-ink' : 'text-slate2 hover:bg-surface hover:text-ink'
            }`}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{label}</span>
          </Link>
        );
      })}
      <StudyMoreMenu pathname={pathname} />
    </nav>
  );
}
